import { AccountBannedError } from "@/errors/AccountBannedError";
import { AccountDeletedError } from "@/errors/AccountDeletedError";
import { clearAccountBannedSession } from "@/helpers/accountBannedSession";
import { clearAccountDeletedSession } from "@/helpers/accountDeletedSession";
import { hasKeycloakConfig, getKeycloakConfig } from "@/lib/keycloak/config";

type KeycloakEnv = {
  baseUrl: string;
  realm: string;
  clientId: string;
  redirectUri: string;
  scope: string;
};

const STORAGE_PREFIX = "kc_oidc:";
const ID_TOKEN_STORAGE_KEY = `${STORAGE_PREFIX}id_token`;
const REFRESH_TOKEN_STORAGE_KEY = `${STORAGE_PREFIX}refresh_token`;
const OAUTH_FLOW_KEYS = new Set(["verifier", "state", "nonce", "appRedirect"]);
const SKIP_AUTO_KEYCLOAK_LOGIN_KEY = `${STORAGE_PREFIX}skip_auto_login`;

function env(...keys: string[]): string {
  for (const key of keys) {
    const value = String((process.env as Record<string, string | undefined>)[key] ?? "").trim();
    if (value) return value;
  }
  return "";
}

export function markSkipAutoKeycloakLoginOnNextVisit(): void {
  sessionStorage.setItem(SKIP_AUTO_KEYCLOAK_LOGIN_KEY, "1");
  try {
    localStorage.setItem(SKIP_AUTO_KEYCLOAK_LOGIN_KEY, "1");
  } catch {
    // ignore
  }
}

export function isSkipAutoKeycloakLoginPending(): boolean {
  if (sessionStorage.getItem(SKIP_AUTO_KEYCLOAK_LOGIN_KEY) === "1") {
    return true;
  }
  try {
    return localStorage.getItem(SKIP_AUTO_KEYCLOAK_LOGIN_KEY) === "1";
  } catch {
    return false;
  }
}

export function consumeSkipAutoKeycloakLogin(): boolean {
  const hit = isSkipAutoKeycloakLoginPending();
  if (hit) {
    sessionStorage.removeItem(SKIP_AUTO_KEYCLOAK_LOGIN_KEY);
    try {
      localStorage.removeItem(SKIP_AUTO_KEYCLOAK_LOGIN_KEY);
    } catch {
      // ignore
    }
    return true;
  }
  return false;
}

function requireEnv(name: string, value: unknown): string {
  const v = String(value ?? "").trim();
  if (!v) throw new Error(`Missing env: ${name}`);
  return v;
}

export function readOAuthCallbackParams(search: string, hash: string): {
  code: string;
  state: string | null;
  error: string;
  error_description: string;
} {
  const out: Record<string, string> = {};
  const merge = (params: URLSearchParams) => {
    params.forEach((v, k) => {
      if (v != null && String(v).trim() !== "" && String(out[k] ?? "") === "") {
        out[k] = v;
      }
    });
  };
  merge(new URLSearchParams(search || ""));
  const qIdx = hash.indexOf("?");
  if (qIdx !== -1) {
    merge(new URLSearchParams(hash.slice(qIdx + 1)));
  }
  return {
    code: out.code ?? "",
    state: out.state ? out.state : null,
    error: out.error ?? "",
    error_description: out.error_description ?? "",
  };
}

export function describeKeycloakOAuthRedirectError(error: string, errorDescription: string): string {
  const err = String(error ?? "").trim();
  const desc = String(errorDescription ?? "").trim();
  const descLower = desc.toLowerCase();

  if (err === "temporarily_unavailable" && descLower.includes("authentication_expired")) {
    return (
      "Your sign-in session expired while you were away (for example, opening email verification in another tab). " +
      "If you already finished verification in another window, continue there-or close this tab and sign in from the login page."
    );
  }
  if (descLower.includes("authentication_expired")) {
    return (
      "That sign-in session expired. If you completed email verification in another tab, use that window-or sign in again from the login page."
    );
  }
  if (err === "login_required" || descLower.includes("login_required")) {
    return "Please sign in again from the login page.";
  }
  if (err === "access_denied") {
    return desc ? `Access denied: ${desc.replace(/_/g, " ")}` : "Access denied.";
  }
  if (desc) {
    return `${err.replace(/_/g, " ")}: ${desc.replace(/_/g, " ")}`;
  }
  return err.replace(/_/g, " ") || "Sign-in failed.";
}

function normalizeOAuthRedirectUri(uri: string): string {
  const s = String(uri ?? "").trim();
  if (!s || !s.includes("#")) return s;

  const hashIdx = s.indexOf("#");
  const base = s.slice(0, hashIdx).replace(/\/+$/, "");
  let frag = s.slice(hashIdx + 1);
  if (!frag) return base;
  if (!frag.startsWith("/")) frag = `/${frag}`;
  return `${base}${frag}`;
}

function isLocalDevOAuthRedirect(uri: string): boolean {
  const s = String(uri ?? "").trim().toLowerCase();
  return s.includes("localhost") || s.includes("127.0.0.1") || s.startsWith("http://0.0.0.0");
}

function resolveRedirectUri(): string {
  const explicit = env(
    "VITE_KEYCLOAK_REDIRECT_URI",
    "VITE_KEYCLOAK_REDIRECT_URL",
    "VUE_APP_KEYCLOAK_REDIRECT_URI",
    "VUE_APP_KEYCLOAK_REDIRECT_URL",
  );

  const apiOrigin = env("VITE_API_URL", "VUE_APP_API_URL").replace(/\/+$/, "");
  const apiOriginWithProtocol =
    apiOrigin && !apiOrigin.startsWith("http") ? `https://${apiOrigin}` : apiOrigin;

  if (explicit && isLocalDevOAuthRedirect(explicit) && typeof window !== "undefined") {
    const proto = window.location.protocol;
    const winOrigin = window.location.origin;

    if (proto === "file:" && apiOriginWithProtocol) {
      return `${apiOriginWithProtocol}/signin`;
    }
    if (proto !== "file:" && winOrigin && !isLocalDevOAuthRedirect(winOrigin)) {
      return normalizeOAuthRedirectUri(`${winOrigin}/signin`);
    }
  }

  if (explicit) return normalizeOAuthRedirectUri(explicit);
  if (typeof window === "undefined") return "";

  const o = window.location.origin;
  if (!o || o === "null") return "";
  return normalizeOAuthRedirectUri(`${o}/signin`);
}

function resolveTokenEndpoint(baseUrl: string, realm: string): string {
  const direct = `${baseUrl}/realms/${encodeURIComponent(realm)}/protocol/openid-connect/token`;
  if (typeof window === "undefined") return direct;
  const host = window.location.hostname.toLowerCase();
  const isLocal = host === "localhost" || host === "127.0.0.1";
  if (!isLocal) return direct;
  return `${window.location.origin}/kc/auth/realms/${encodeURIComponent(realm)}/protocol/openid-connect/token`;
}

function resolveAccountEndpoint(baseUrl: string, realm: string): string {
  const direct = `${baseUrl}/realms/${encodeURIComponent(realm)}/account`;
  if (typeof window === "undefined") return direct;
  const host = window.location.hostname.toLowerCase();
  const isLocal = host === "localhost" || host === "127.0.0.1";
  if (!isLocal) return direct;
  return `${window.location.origin}/kc/auth/realms/${encodeURIComponent(realm)}/account`;
}

export function resolvePostLogoutRedirectUri(): string | null {
  const explicit = env(
    "VITE_KEYCLOAK_POST_LOGOUT_REDIRECT_URI",
    "VITE_KEYCLOAK_POST_LOGOUT_REDIRECT_URL",
    "VUE_APP_KEYCLOAK_POST_LOGOUT_REDIRECT_URI",
    "VUE_APP_KEYCLOAK_POST_LOGOUT_REDIRECT_URL",
  );
  if (explicit.toLowerCase() === "omit" || explicit === "-") return null;

  const apiOrigin = env("VITE_API_URL", "VUE_APP_API_URL").replace(/\/+$/, "");
  const apiOriginWithProtocol =
    apiOrigin && !apiOrigin.startsWith("http") ? `https://${apiOrigin}` : apiOrigin;

  if (explicit && isLocalDevOAuthRedirect(explicit) && typeof window !== "undefined") {
    const proto = window.location.protocol;
    const winOrigin = window.location.origin;

    if (proto === "file:" && apiOriginWithProtocol) {
      return normalizeOAuthRedirectUri(`${apiOriginWithProtocol}/signin`);
    }
    if (proto !== "file:" && winOrigin && !isLocalDevOAuthRedirect(winOrigin)) {
      return normalizeOAuthRedirectUri(`${winOrigin}/signin`);
    }
  }

  if (explicit) return normalizeOAuthRedirectUri(explicit);
  if (typeof window === "undefined") return null;

  const o = window.location.origin;
  if (!o || o === "null") return null;
  return normalizeOAuthRedirectUri(`${o}/signin`);
}

export function isKeycloakEnabled(): boolean {
  const baseUrl = env("VITE_KEYCLOAK_BASE_URL", "VITE_KEYCLOAK_URL", "VUE_APP_KEYCLOAK_BASE_URL");
  const realm = env("VITE_KEYCLOAK_REALM", "VUE_APP_KEYCLOAK_REALM");
  const clientId = env("VITE_KEYCLOAK_CLIENT_ID", "VUE_APP_KEYCLOAK_CLIENT_ID");
  const redirectUri = resolveRedirectUri();
  return Boolean(baseUrl && realm && clientId && redirectUri);
}

function getKeycloakEnv(): KeycloakEnv {
  const baseUrl = requireEnv(
    "KEYCLOAK_BASE_URL|KEYCLOAK_URL",
    env("VITE_KEYCLOAK_BASE_URL", "VITE_KEYCLOAK_URL", "VUE_APP_KEYCLOAK_BASE_URL", "VUE_APP_KEYCLOAK_URL"),
  );
  const realm = requireEnv("KEYCLOAK_REALM", env("VITE_KEYCLOAK_REALM", "VUE_APP_KEYCLOAK_REALM"));
  const clientId = requireEnv(
    "KEYCLOAK_CLIENT_ID",
    env("VITE_KEYCLOAK_CLIENT_ID", "VUE_APP_KEYCLOAK_CLIENT_ID"),
  );
  const redirectUri = requireEnv("KEYCLOAK_REDIRECT_URI|KEYCLOAK_REDIRECT_URL", resolveRedirectUri());
  const scope = env("VITE_KEYCLOAK_SCOPE", "VUE_APP_KEYCLOAK_SCOPE") || "openid";
  return { baseUrl: baseUrl.replace(/\/+$/, ""), realm, clientId, redirectUri, scope };
}

function base64UrlEncode(bytes: ArrayBuffer): string {
  const arr = new Uint8Array(bytes);
  let bin = "";
  for (let i = 0; i < arr.length; i += 1) {
    bin += String.fromCharCode(arr[i]);
  }
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function randomString(len = 64): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes.buffer);
}

async function sha256(input: string): Promise<ArrayBuffer> {
  const enc = new TextEncoder().encode(input);
  return await crypto.subtle.digest("SHA-256", enc);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const hash = await sha256(verifier);
  return base64UrlEncode(hash);
}

function storageKey(key: string): string {
  return `${STORAGE_PREFIX}${key}`;
}

function setSession(key: string, value: string) {
  const full = storageKey(key);
  sessionStorage.setItem(full, value);
  if (OAUTH_FLOW_KEYS.has(key)) {
    try {
      localStorage.setItem(full, value);
    } catch {
      // ignore
    }
  }
}

function getSession(key: string): string | null {
  const full = storageKey(key);
  const fromSession = sessionStorage.getItem(full);
  if (fromSession != null && fromSession !== "") return fromSession;
  if (OAUTH_FLOW_KEYS.has(key)) {
    try {
      return localStorage.getItem(full);
    } catch {
      return null;
    }
  }
  return null;
}

function clearSession(keys: string[]) {
  keys.forEach((k) => {
    const full = storageKey(k);
    sessionStorage.removeItem(full);
    if (OAUTH_FLOW_KEYS.has(k)) {
      try {
        localStorage.removeItem(full);
      } catch {
        // ignore
      }
    }
  });
}

export function persistKeycloakTokens(tokens: { idToken?: string; refreshToken?: string }) {
  if (tokens.idToken) {
    sessionStorage.setItem(ID_TOKEN_STORAGE_KEY, tokens.idToken);
    try {
      localStorage.setItem(ID_TOKEN_STORAGE_KEY, tokens.idToken);
    } catch {
      // ignore
    }
  }
  if (tokens.refreshToken) {
    sessionStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, tokens.refreshToken);
    try {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, tokens.refreshToken);
    } catch {
      // ignore
    }
  }
}

export function getStoredKeycloakRefreshToken(): string {
  const fromSession = sessionStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) || "";
  if (fromSession) return fromSession;
  try {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function getStoredAccessToken(): string {
  try {
    const raw = localStorage.getItem("token");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return typeof parsed === "string" ? parsed : String(parsed || "");
  } catch {
    const raw = localStorage.getItem("token");
    return raw || "";
  }
}

function parseJwtExp(token: string): number {
  try {
    const payload = token.split(".")[1];
    if (!payload) return 0;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return Number(json?.exp || 0);
  } catch {
    return 0;
  }
}

function isJwtExpiredOrNearExpiry(token: string, skewSeconds = 30): boolean {
  const exp = parseJwtExp(token);
  if (!exp) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp <= nowSeconds + skewSeconds;
}

export async function refreshKeycloakAccessToken(
  refreshToken: string,
): Promise<KeycloakTokenResponse> {
  if (!refreshToken) {
    throw new Error("Missing refresh token");
  }
  const keycloak = getKeycloakEnv();
  const tokenUrl = resolveTokenEndpoint(keycloak.baseUrl, keycloak.realm);
  const body = new URLSearchParams();
  body.set("grant_type", "refresh_token");
  body.set("client_id", keycloak.clientId);
  body.set("refresh_token", refreshToken);

  const res = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Token refresh failed (${res.status}): ${text || res.statusText}`);
  }
  const json = (await res.json()) as KeycloakTokenResponse;
  if (!json?.access_token) {
    throw new Error("No access_token in refresh response");
  }
  return json;
}

export async function updateKeycloakUsername(username: string, email?: string): Promise<void> {
  const normalizedUsername = String(username || "").trim().toLowerCase();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!normalizedUsername) {
    throw new Error("Username is required for Keycloak sync");
  }
  if (!isKeycloakEnabled()) return;

  const keycloak = getKeycloakEnv();
  let accessToken = getStoredAccessToken();
  const refreshUsingStoredToken = async (): Promise<string> => {
    const refreshToken = getStoredKeycloakRefreshToken();
    if (!refreshToken) {
      throw new Error("Keycloak session expired. Please sign in again.");
    }
    const refreshed = await refreshKeycloakAccessToken(refreshToken);
    const newAccessToken = refreshed.access_token;
    localStorage.setItem("token", JSON.stringify(newAccessToken));
    persistKeycloakTokens({
      idToken: refreshed.id_token,
      refreshToken: refreshed.refresh_token || refreshToken,
    });
    return newAccessToken;
  };

  if (!accessToken || isJwtExpiredOrNearExpiry(accessToken)) {
    accessToken = await refreshUsingStoredToken();
  }

  const accountUrl = resolveAccountEndpoint(keycloak.baseUrl, keycloak.realm);
  const doGetProfile = async (token: string) =>
    fetch(accountUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

  const doUpdate = async (token: string, payload: Record<string, unknown>) =>
    fetch(accountUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

  let profileRes = await doGetProfile(accessToken);
  if (profileRes.status === 401) {
    accessToken = await refreshUsingStoredToken();
    profileRes = await doGetProfile(accessToken);
  }

  if (!profileRes.ok) {
    const text = await profileRes.text().catch(() => "");
    throw new Error(text || `Unable to load Keycloak account (${profileRes.status})`);
  }

  const currentProfile = (await profileRes.json().catch(() => ({}))) as Record<
    string,
    unknown
  >;
  const pickString = (value: unknown): string | undefined => {
    const v = String(value ?? "").trim();
    return v ? v : undefined;
  };
  const updatePayload: Record<string, unknown> = {
    username: normalizedUsername,
  };
  const firstName = pickString(currentProfile.firstName);
  const lastName = pickString(currentProfile.lastName);
  const existingEmail = pickString(currentProfile.email);
  if (firstName !== undefined) updatePayload.firstName = firstName;
  if (lastName !== undefined) updatePayload.lastName = lastName;
  if (normalizedEmail) {
    updatePayload.email = normalizedEmail;
  } else if (existingEmail !== undefined) {
    updatePayload.email = existingEmail;
  }
  if (
    currentProfile.attributes &&
    typeof currentProfile.attributes === "object" &&
    !Array.isArray(currentProfile.attributes)
  ) {
    updatePayload.attributes = currentProfile.attributes;
  }

  let res = await doUpdate(accessToken, updatePayload);
  if (res.status === 401) {
    accessToken = await refreshUsingStoredToken();
    res = await doUpdate(accessToken, updatePayload);
  }
  if (res.status === 400) {
    // Some realms only accept minimal account updates.
    res = await doUpdate(accessToken, { username: normalizedUsername });
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let parsed: any = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    const errors = Array.isArray(parsed?.errors) ? parsed.errors : [];
    const hasReadonlyUsername = errors.some(
      (e: any) => e?.field === "username" && e?.errorMessage === "error-user-attribute-read-only",
    );
    const hasRequiredEmail = errors.some(
      (e: any) => e?.field === "email" && e?.errorMessage === "error-user-attribute-required",
    );
    if (hasReadonlyUsername) {
      throw new Error(
        "Keycloak realm policy marks username as read-only. Username cannot be changed from account endpoint.",
      );
    }
    if (hasRequiredEmail) {
      throw new Error("Keycloak requires email while updating account profile.");
    }
    throw new Error(text || `Keycloak username update failed (${res.status})`);
  }
}

export async function startKeycloakLogin(params?: {
  redirectPath?: string;
  promptLogin?: boolean;
  forceRegister?: boolean;
  extraAuthParams?: Record<string, string>;
}) {
  if (!isKeycloakEnabled()) {
    throw new Error("Keycloak is not configured");
  }
  const keycloak = getKeycloakEnv();
  if (params?.redirectPath) setSession("appRedirect", params.redirectPath);

  const verifier = randomString(64);
  const challenge = await pkceChallenge(verifier);
  const state = randomString(32);
  const nonce = randomString(32);

  setSession("verifier", verifier);
  setSession("state", state);
  setSession("nonce", nonce);

  const authPath = params?.forceRegister
    ? "protocol/openid-connect/registrations"
    : "protocol/openid-connect/auth";
  const url = new URL(
    `${keycloak.baseUrl}/realms/${encodeURIComponent(keycloak.realm)}/${authPath}`,
  );
  url.searchParams.set("client_id", keycloak.clientId);
  url.searchParams.set("redirect_uri", keycloak.redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("response_mode", "query");
  url.searchParams.set("scope", keycloak.scope);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("state", state);
  url.searchParams.set("nonce", nonce);
  if (params?.promptLogin) {
    url.searchParams.set("prompt", "login");
    url.searchParams.set("max_age", "0");
  }
  if (params?.forceRegister) {
    // Keep hints for themes/providers that honor them.
    url.searchParams.set("kc_action", "register");
    url.searchParams.set("screen_hint", "signup");
  }
  if (params?.extraAuthParams) {
    Object.entries(params.extraAuthParams).forEach(([k, v]) => {
      const key = String(k || "").trim();
      const val = String(v || "").trim();
      if (key && val) {
        url.searchParams.set(key, val);
      }
    });
  }
  window.location.assign(url.toString());
}

export async function startKeycloakChangePassword(params?: {
  redirectPath?: string;
}) {
  if (!isKeycloakEnabled()) {
    throw new Error("Keycloak is not configured");
  }
  const keycloak = getKeycloakEnv();
  if (params?.redirectPath) setSession("appRedirect", params.redirectPath);

  const verifier = randomString(64);
  const challenge = await pkceChallenge(verifier);
  const state = randomString(32);
  const nonce = randomString(32);

  setSession("verifier", verifier);
  setSession("state", state);
  setSession("nonce", nonce);

  const url = new URL(
    `${keycloak.baseUrl}/realms/${encodeURIComponent(keycloak.realm)}/protocol/openid-connect/auth`,
  );
  url.searchParams.set("client_id", keycloak.clientId);
  url.searchParams.set("redirect_uri", keycloak.redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("response_mode", "query");
  url.searchParams.set("scope", keycloak.scope);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("state", state);
  url.searchParams.set("nonce", nonce);
  url.searchParams.set("kc_action", "UPDATE_PASSWORD");
  window.location.assign(url.toString());
}

export type KeycloakTokenResponse = {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type?: string;
  expires_in?: number;
  refresh_expires_in?: number;
  scope?: string;
};

export async function exchangeKeycloakCodeForToken(args: {
  code: string;
  state: string | null;
}): Promise<KeycloakTokenResponse> {
  const keycloak = getKeycloakEnv();
  const expectedState = getSession("state");
  const verifier = getSession("verifier");

  if (!args.code) throw new Error("Missing authorization code");
  if (!args.state) throw new Error("Missing OAuth state. Please sign in again from the login page.");
  if (!expectedState) {
    throw new Error("Sign-in data was lost. Please sign in again from the login page.");
  }
  if (args.state !== expectedState) {
    throw new Error("Login state does not match. Please sign in again from the login page.");
  }
  if (!verifier) {
    throw new Error("Missing sign-in verifier. Please sign in again from the login page.");
  }

  const tokenUrl = resolveTokenEndpoint(keycloak.baseUrl, keycloak.realm);
  const body = new URLSearchParams();
  body.set("grant_type", "authorization_code");
  body.set("client_id", keycloak.clientId);
  body.set("code", args.code);
  body.set("redirect_uri", keycloak.redirectUri);
  body.set("code_verifier", verifier);

  const res = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Token exchange failed (${res.status}): ${text || res.statusText}`);
  }
  const json = (await res.json()) as KeycloakTokenResponse;
  if (!json?.access_token) throw new Error("No access_token in token response");

  clearSession(["verifier", "state", "nonce"]);
  return json;
}

export function setPostLoginRedirect(path: string): void {
  const normalized = String(path || "").trim();
  if (!normalized || normalized.startsWith("//")) return;
  setSession("appRedirect", normalized);
}

export function consumePostLoginRedirect(fallback = "/profile/feed"): string {
  const v = getSession("appRedirect");
  if (v) clearSession(["appRedirect"]);
  return v || fallback;
}

export async function verifyKeycloakTokenWithApi(
  accessToken: string,
): Promise<{ token: string; user: Record<string, unknown> }> {
  if (!accessToken) throw new Error("Missing access token");
  const apiHost = env("VUE_APP_API_URL");
  const basePath = apiHost ? `https://${apiHost}/api` : "/api";
  const res = await fetch(`${basePath}/users/me`, {
    method: "GET",
    headers: {
      accept: "*/*",
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const payload = await res.json().catch(() => ({} as Record<string, unknown>));

  if (!res.ok) {
    if (res.status === 403) {
      throw new AccountBannedError();
    }
    if (res.status === 404) {
      const message = String((payload as Record<string, any>)?.message ?? "")
        .trim()
        .toLowerCase();
      if (message === "account not found" || message.includes("account not found")) {
        throw new AccountDeletedError();
      }
    }
    const message =
      (payload as Record<string, any>)?.message || `Token verification failed (${res.status})`;
    throw new Error(message);
  }

  const body = (payload as Record<string, any>)?.data ?? payload;
  const user = body?.user ?? body?.data?.user ?? body ?? null;
  const nextToken = accessToken;
  if (!user || typeof user !== "object") {
    throw new Error("User not found in callback response");
  }
  clearAccountBannedSession();
  clearAccountDeletedSession();
  return { token: String(nextToken), user: user as Record<string, unknown> };
}

export function keycloakLogout(options?: { redirectUri?: string }) {
  if (!isKeycloakEnabled()) return;
  const keycloak = getKeycloakEnv();
  const idTokenHint = sessionStorage.getItem(ID_TOKEN_STORAGE_KEY) || "";
  const fromOptions = options?.redirectUri?.trim();
  const resolved = fromOptions ? normalizeOAuthRedirectUri(fromOptions) : resolvePostLogoutRedirectUri();

  clearSession(["verifier", "state", "nonce", "appRedirect", "refresh_token"]);
  sessionStorage.removeItem(ID_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
  markSkipAutoKeycloakLoginOnNextVisit();

  const logoutUrl = new URL(
    `${keycloak.baseUrl}/realms/${encodeURIComponent(keycloak.realm)}/protocol/openid-connect/logout`,
  );
  logoutUrl.searchParams.set("client_id", keycloak.clientId);
  if (resolved) logoutUrl.searchParams.set("post_logout_redirect_uri", resolved);
  if (idTokenHint) logoutUrl.searchParams.set("id_token_hint", idTokenHint);
  window.location.assign(logoutUrl.toString());
}

export function isKeycloakSsoSyncEnabled(): boolean {
  return hasKeycloakConfig() || isKeycloakEnabled();
}

export function hasValidStoredSession(): boolean {
  try {
    if (localStorage.getItem("token")) return true;
  } catch {
    // ignore
  }
  return !!getStoredKeycloakRefreshToken();
}

function resolveSilentCheckRedirectUri(): string {
  if (typeof window === "undefined") return "";
  const o = window.location.origin;
  if (!o || o === "null") return "";
  return `${o}/silent-check-sso.html`;
}

function setSilentProbe(key: string, value: string): void {
  sessionStorage.setItem(storageKey(key), value);
}

function getSilentProbe(key: string): string | null {
  return sessionStorage.getItem(storageKey(key));
}

function clearSilentProbe(): void {
  ["silent_verifier", "silent_state", "silent_code"].forEach((k) => {
    sessionStorage.removeItem(storageKey(k));
  });
}

export function clearLocalSessionForSsoLogout(): void {
  clearAccountBannedSession();
  clearAccountDeletedSession();
  sessionStorage.removeItem(ID_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  clearSilentProbe();
  clearSession(["verifier", "state", "nonce", "appRedirect", "refresh_token"]);
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export type KeycloakSilentSsoProbeResult =
  | "authenticated"
  | "unauthenticated"
  | "unknown";

function resolveProbeKeycloakEnv(silentRedirect: string): KeycloakEnv | null {
  try {
    return getKeycloakEnv();
  } catch {
    if (!hasKeycloakConfig()) return null;
    const { url, realm, clientId } = getKeycloakConfig();
    return {
      baseUrl: url.replace(/\/+$/, ""),
      realm,
      clientId,
      redirectUri: silentRedirect,
      scope: env("VITE_KEYCLOAK_SCOPE", "VUE_APP_KEYCLOAK_SCOPE") || "openid",
    };
  }
}

export function probeKeycloakSilentSso(): Promise<KeycloakSilentSsoProbeResult> {
  if (!isKeycloakSsoSyncEnabled() || typeof window === "undefined") {
    return Promise.resolve("unknown");
  }

  const silentRedirect = resolveSilentCheckRedirectUri();
  if (!silentRedirect) return Promise.resolve("unknown");

  const probeEnv = resolveProbeKeycloakEnv(silentRedirect);
  if (!probeEnv) return Promise.resolve("unknown");

  return new Promise((resolve) => {
    const verifier = randomString(64);
    const state = randomString(32);
    const nonce = randomString(32);

    setSilentProbe("silent_verifier", verifier);
    setSilentProbe("silent_state", state);

    let settled = false;
    const finish = (result: KeycloakSilentSsoProbeResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "sso-check");
    iframe.style.cssText = "display:none;width:0;height:0;border:0";

    const timeout = window.setTimeout(() => finish("unknown"), 12000);

    const cleanup = () => {
      window.clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      iframe.remove();
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (typeof event.data !== "string" || !event.data.includes("silent-check-sso")) {
        return;
      }

      let parsed: URL;
      try {
        parsed = new URL(event.data);
      } catch {
        finish("unknown");
        return;
      }

      const params = readOAuthCallbackParams(parsed.search, parsed.hash);
      if (
        params.error === "login_required" ||
        params.error === "interaction_required"
      ) {
        clearSilentProbe();
        finish("unauthenticated");
        return;
      }
      if (params.error) {
        clearSilentProbe();
        finish("unknown");
        return;
      }
      if (params.code && params.state === getSilentProbe("silent_state")) {
        setSilentProbe("silent_code", params.code);
        finish("authenticated");
        return;
      }
      clearSilentProbe();
      finish("unknown");
    };

    window.addEventListener("message", onMessage);
    document.body.appendChild(iframe);

    void pkceChallenge(verifier).then((challenge) => {
      const url = new URL(
        `${probeEnv.baseUrl}/realms/${encodeURIComponent(probeEnv.realm)}/protocol/openid-connect/auth`,
      );
      url.searchParams.set("client_id", probeEnv.clientId);
      url.searchParams.set("redirect_uri", silentRedirect);
      url.searchParams.set("response_type", "code");
      url.searchParams.set("response_mode", "fragment");
      url.searchParams.set("scope", probeEnv.scope);
      url.searchParams.set("prompt", "none");
      url.searchParams.set("code_challenge_method", "S256");
      url.searchParams.set("code_challenge", challenge);
      url.searchParams.set("state", state);
      url.searchParams.set("nonce", nonce);
      iframe.src = url.toString();
    });
  });
}

export function hasPendingOAuthFlow(): boolean {
  return !!(getSession("verifier") || getSession("state"));
}

export function isOAuthCallbackRoute(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path === "/signin" && /(?:^|[?#&])(code|state)=/.test(
    `${window.location.search}${window.location.hash}`
  );
}

/**
 * Re-validate the local session after reload or when another app may have ended SSO.
 * Returns false when refresh failed and local session was cleared.
 */
export async function refreshKeycloakSessionOnReload(): Promise<boolean> {
  if (!isKeycloakSsoSyncEnabled() || !hasValidStoredSession()) {
    return true;
  }

  if (hasKeycloakConfig()) {
    try {
      const { initKeycloakSession } = await import("@/lib/keycloak");
      const kc = await initKeycloakSession();
      if (!kc?.authenticated) {
        clearLocalSessionForSsoLogout();
        return false;
      }
      if (kc.isTokenExpired(30)) {
        await kc.updateToken(30);
      }
      return true;
    } catch {
      clearLocalSessionForSsoLogout();
      return false;
    }
  }

  const refreshToken = getStoredKeycloakRefreshToken();
  if (!refreshToken) {
    return true;
  }

  try {
    const refreshed = await refreshKeycloakAccessToken(refreshToken);
    const verified = await verifyKeycloakTokenWithApi(refreshed.access_token);
    persistKeycloakTokens({
      idToken: refreshed.id_token,
      refreshToken: refreshed.refresh_token || refreshToken,
    });
    try {
      localStorage.setItem("token", JSON.stringify(verified.token));
      localStorage.setItem("user", JSON.stringify(verified.user));
    } catch {
      // ignore
    }
    return true;
  } catch {
    clearLocalSessionForSsoLogout();
    return false;
  }
}
